import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { Appointment, AppointmentStatus, Product } from '../types';
import { updateAppointment } from '../lib/api';
import { useToast } from '../ui/UIContext';

const STATUS_BADGE: Record<AppointmentStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-200 text-gray-700',
};

interface Props {
  appointment: Appointment;
  product: Product | null;
  busy: boolean;
  onClose: () => void;
  onStatusChange: (status: AppointmentStatus) => void;
  onDelete: () => void;
  onUpdated: (appointment: Appointment) => void;
}

export function AppointmentDetailDrawer({
  appointment,
  product,
  busy,
  onClose,
  onStatusChange,
  onDelete,
  onUpdated,
}: Props) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [notes, setNotes] = useState(appointment.notes);
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setNotes(appointment.notes);
  }, [appointment.id, appointment.notes]);

  const dirty = notes !== appointment.notes;

  async function handleSaveNotes() {
    setSavingNotes(true);
    try {
      const updated = await updateAppointment(appointment.id, { notes });
      onUpdated(updated);
      showToast(t('admin.detail.notesSaved'), 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save', 'error');
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-md bg-white shadow-xl overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{t('admin.detail.title')}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div>
            <span
              className={`inline-flex px-2 py-1 rounded text-xs font-semibold ${STATUS_BADGE[appointment.status]}`}
            >
              {t(`admin.status.${appointment.status.toLowerCase()}`)}
            </span>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('admin.detail.client')}
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-800">
              <User className="w-4 h-4 text-gray-400" />
              {appointment.name}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Mail className="w-4 h-4 text-gray-400" />
              <a className="hover:underline" href={`mailto:${appointment.email}`}>
                {appointment.email}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Phone className="w-4 h-4 text-gray-400" />
              <a className="hover:underline" href={`tel:${appointment.phone}`}>
                {appointment.phone}
              </a>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('admin.detail.booking')}
            </h3>
            {product && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                <img
                  src={product.imageUrl}
                  alt=""
                  className="w-12 h-12 rounded object-cover bg-gray-100"
                />
                <div>
                  <div className="font-medium text-gray-900 text-sm">{product.title}</div>
                  <div className="text-xs text-gray-500">{product.measurementDuration}</div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Calendar className="w-4 h-4 text-gray-400" />
              {appointment.appointmentDate}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Clock className="w-4 h-4 text-gray-400" />
              {t(`form.timeSlots.${appointment.timeSlot}`)}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('admin.detail.notes')}
            </h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-gray-300 text-gray-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('admin.detail.notesPlaceholder')}
            />
            <button
              onClick={handleSaveNotes}
              disabled={!dirty || savingNotes}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {savingNotes ? t('admin.detail.saving') : t('admin.detail.saveNotes')}
            </button>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('admin.detail.timeline')}
            </h3>
            <div className="text-xs text-gray-500">
              {t('admin.detail.created')}: {new Date(appointment.createdAt).toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">
              {t('admin.detail.updated')}: {new Date(appointment.updatedAt).toLocaleString()}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('admin.detail.actions')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {appointment.status !== 'CONFIRMED' && appointment.status !== 'COMPLETED' && (
                <button
                  onClick={() => onStatusChange('CONFIRMED')}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" />
                  {t('admin.actions.confirm')}
                </button>
              )}
              {appointment.status === 'CONFIRMED' && (
                <button
                  onClick={() => onStatusChange('COMPLETED')}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {t('admin.actions.complete')}
                </button>
              )}
              {appointment.status !== 'PENDING' &&
                appointment.status !== 'CANCELLED' &&
                appointment.status !== 'COMPLETED' && (
                  <button
                    onClick={() => onStatusChange('PENDING')}
                    disabled={busy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                  >
                    <Clock className="w-4 h-4" />
                    {t('admin.actions.reopen')}
                  </button>
                )}
              {appointment.status !== 'CANCELLED' && (
                <button
                  onClick={() => onStatusChange('CANCELLED')}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                  {t('admin.actions.cancel')}
                </button>
              )}
              <button
                onClick={onDelete}
                disabled={busy}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {t('admin.actions.delete')}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
